/**
 * Pre-permission rationale dialog for the Smart Auto-Release feature.
 *
 * Shown ONCE when the user flips the master toggle ON. Tap Continue →
 * caller triggers the OS permission prompt. Tap Not now → caller sets
 * rationaleDeclinedAt server-side and never auto-prompts again.
 *
 * Wording covers what an app-store reviewer (and a privacy-conscious
 * resident) needs to know:
 *   1. WHEN it runs — building working days × user-chosen active window
 *   2. WHERE the work happens — on-device until a release fires
 *   3. WHY false positives are unlikely — vehicle-motion detection
 *   4. HOW it can be undone — 2-minute cancel window
 *   5. WHO opts in per spot — covered explicitly so multi-spot owners
 *      don't fear the master toggle nuking all their spots at once
 *
 * Bullets are i18n strings, NOT computed from settings — the dialog
 * shows before the user has configured anything, and the generic copy
 * lets the panel itself reveal the concrete window/threshold next.
 */
import { useTranslation } from '../i18n';

interface Props {
  open:        boolean;
  onContinue:  () => void;
  onDecline:   () => void;
}

export default function AutoReleaseRationaleDialog({ open, onContinue, onDecline }: Props) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position:       'fixed',
        inset:          0,
        backgroundColor:'rgba(0,0,0,0.5)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        zIndex:         50,
        padding:        '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onDecline(); }}
    >
      <div style={{
        backgroundColor: '#fff',
        borderRadius:    '16px',
        padding:         '20px',
        maxWidth:        '380px',
        width:           '100%',
        maxHeight:       '60vh',
        display:         'flex',
        flexDirection:   'column',
      }}>
        <div style={{ fontSize: '28px', textAlign: 'center', marginBottom: '8px', flexShrink: 0 }}>📍</div>
        <h2 style={{ fontSize: '16px', fontWeight: 700, textAlign: 'center', marginBottom: '10px', color: '#111', flexShrink: 0 }}>
          {t('auto_release_rationale_title') || 'Enable Smart Auto-Release'}
        </h2>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <p style={{ fontSize: '13px', color: '#374151', marginBottom: '12px', lineHeight: 1.5 }}>
            {t('auto_release_rationale_intro') || 'To free your spot automatically when you leave, the app needs background location. Here\'s exactly how it\'s used:'}
          </p>
          <ul style={{
            listStyle:    'none',
            padding:      0,
            margin:       '0 0 12px 0',
            fontSize:     '12px',
            color:        '#374151',
            lineHeight:   1.5,
          }}>
            {[
              t('auto_release_rationale_when')    || '✓ Only on your building\'s working days during the active window you set',
              t('auto_release_rationale_local')   || '✓ Location is checked on your phone — nothing is sent to us unless you actually leave the building',
              t('auto_release_rationale_vehicle') || '✓ Releases only when you\'re at least 4 km from your building. Don\'t enable if you bike/transit/walk a short distance to work',
              t('auto_release_rationale_cancel')  || '✓ Two-minute window to undo right after a release, in case you\'re heading back',
              t('auto_release_rationale_perspot') || '✓ You choose which of your spots to include — manage per-spot in Spot details',
              t('auto_release_rationale_modes')   || '✓ Want extra control? Enable "Ask before releasing" to confirm each time, instead of automatic',
              t('auto_release_rationale_weekend') || '✓ Working weekends? Toggle "Include weekends" to extend the schedule to all 7 days',
              t('auto_release_rationale_reclaim') || '✓ When you return home, your spot is automatically reclaimed (if it\'s free) — no need to do anything',
              t('auto_release_rationale_quiet')   || '✓ Driving? Toggle "Quiet mode" to skip the cancel push so it doesn\'t interrupt navigation',
            ].map((line) => (
              <li key={line} style={{ marginBottom: '5px' }}>{line}</li>
            ))}
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0, paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
          <button
            onClick={onDecline}
            style={{
              padding:        '10px 16px',
              border:         'none',
              background:     '#f3f4f6',
              color:          '#374151',
              borderRadius:   '8px',
              fontWeight:     600,
              fontSize:       '14px',
              cursor:         'pointer',
            }}
          >
            {t('not_now') || 'Not now'}
          </button>
          <button
            onClick={onContinue}
            style={{
              padding:        '10px 18px',
              border:         'none',
              background:     '#005bc0',
              color:          '#fff',
              borderRadius:   '8px',
              fontWeight:     700,
              fontSize:       '14px',
              cursor:         'pointer',
            }}
          >
            {t('continue') || 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
